import {trans} from '@frontend/helpers'
import type {ColorAdPropertyOptionExtras} from '@frontend/modules/private/ads/MyAdsModuleTypes'
import type {AdPropertyConfigForFilters} from '@frontend/modules/public/ads/AdsModuleTypes'
import {iconCheckboxCheck} from '@shared/helpers/icons'
import clsx from 'clsx'
import {useState} from 'react'
import {FadeIn} from 'swayok-react-mdb-ui-kit/components/Animation/FadeIn'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {toggleValueInArray} from 'swayok-react-mdb-ui-kit/helpers/form/toggleValueInArray'
import type {FormSelectOption} from 'swayok-react-mdb-ui-kit/types/Common'
import {FilterLabel} from './FilterLabel'

interface Props {
    config: AdPropertyConfigForFilters
    values: (string | number | null)[]
    onChange: (name: string, values: (string | number | null)[]) => void
    disabled?: boolean
    noLabel?: boolean
    visibleOptionsCount?: number
    className?: string
}

// Фильтр выбора нескольких цветов из списка.
export function ColorsFilter(props: Props) {

    const {
        config,
        values = [],
        onChange,
        disabled,
        noLabel,
        visibleOptionsCount = 4,
        className = 'pb-4',
    } = props

    const [
        isAllOptionsVisible,
        setIsAllOptionsVisible,
    ] = useState<boolean>(false)

    const {
        label,
        className: classNameFromConfig,
        options,
    } = config.props

    if (!options) {
        // Нет опций - нечего показывать.
        return null
    }

    // Выбранные цвета не прячутся под кнопку "Показать все".
    const visibleCount: number = Math.max(
        visibleOptionsCount,
        ...values.map(value => options.findIndex(option => option.value === value) + 1)
    )
    const hiddenOptions = options.slice(visibleCount)

    // Отрисовка одной опции.
    const renderOption = (option: FormSelectOption<string | number | null>) => (
        <div
            key={option.value}
            className="d-flex flex-row align-items-center gap-2 clickable"
            onClick={() => {
                if (disabled) {
                    return
                }
                onChange(
                    config.name,
                    toggleValueInArray(
                        values,
                        option.value
                    )
                )
            }}
        >
            <div
                className="offered-items-color-filter-swatch rounded-6 border flex-center flex-shrink-0"
                style={{
                    background: (option.extra as ColorAdPropertyOptionExtras)?.background,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                }}
            >
                <FadeIn visible={values.includes(option.value)}>
                    <div className="offered-item-color-filter-check rounded-3 flex-center">
                        <Icon
                            path={iconCheckboxCheck}
                            color="white"
                        />
                    </div>
                </FadeIn>
            </div>
            <span className="text-gray">{option.label}</span>
        </div>
    )

    return (
        <div
            className={clsx(
                className,
                classNameFromConfig
            )}
        >
            {!noLabel && (
                <FilterLabel
                    label={label}
                    showResetButton={values.length > 0}
                    disabled={disabled}
                    onReset={() => onChange(config.name, [])}
                />
            )}
            <div className="d-flex flex-column gap-2">
                {options.slice(0, visibleCount).map(renderOption)}
            </div>
            {hiddenOptions.length > 0 && (
                <>
                    <Collapse show={isAllOptionsVisible}>
                        <div className="d-flex flex-column gap-2 pt-2">
                            {hiddenOptions.map(renderOption)}
                        </div>
                    </Collapse>
                    <Button
                        color="link"
                        onClick={() => setIsAllOptionsVisible(visible => !visible)}
                        className="link-blue mt-2"
                    >
                        {
                            isAllOptionsVisible
                                ? trans().ads.list.filters.hide_options
                                : trans().ads.list.filters.show_all_options
                        }
                    </Button>
                </>
            )}
        </div>
    )
}
